import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import { RMSCommonDialog, RMSConfirmationDialog } from 'features/shared/components/dialog';
import { RMSCheckbox } from 'features/shared/components/form-controls';
import SearchPersonAutoComplete from 'features/shared/components/modules/SearchPersonAutoComplete';
import SearchPlaceAutoComplete from 'features/shared/components/modules/SearchPlaceAutoComplete';
import RMSRecordHistoryButton from 'features/shared/components/modules/RMSRecordHistoryButton';
import CreateFormResolver from 'features/shared/utility/createFormResolver';
import { executeApiAction } from 'features/shared/utility/apiHelper';
import useConfirmDialog from 'features/shared/utility/useConfirmDialog';
import { notify } from 'reducers/NotifierReducer';
import { VEHICLE_TOWING_DEFAULT_VALUES } from '../../constants/vehicleTowingFormConstants';
import vehicleTowingSchema from '../../schema/vehicleTowingSchema';
import { getVehicleTowingRecord, saveVehicleTowingRecord } from '../../services/vehicleService';

const VehicleTowingDialog = ({ ptsVehicleID, towing = null, canSave, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState(null);
  const methods = useForm({
    resolver: CreateFormResolver(vehicleTowingSchema),
    defaultValues: VEHICLE_TOWING_DEFAULT_VALUES,
    disabled: !canSave,
  });
  const [driverId, placeId] = useWatch({
    control: methods.control,
    name: ['ptsDriverID', 'ptsPlaceID'],
  });

  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const save = methods.handleSubmit((data) => {
    if (!canSave) return;
    return executeApiAction({
      dispatch,
      setLoading,
      action: () =>
        saveVehicleTowingRecord(
          {
            ptsVehicleID,
            IsTowed: data.IsTowed,
            ptsPlaceID: data.ptsPlaceID,
            ptsDriverID: data.ptsDriverID ?? null,
            ptsEventID: data.ptsEventID ?? null,
          },
          towing?.ptsTowingID
        ),
      onSuccess: (result) => {
        dispatch(notify('Towing saved successfully', 'success'));
        onSaved(result);
      },
    });
  });

  const close = () => {
    if (methods.formState.isDirty)
      requestConfirm(
        'You have unsaved towing changes. Close without saving?',
        onClose,
        'Unsaved Changes'
      );
    else onClose();
  };

  useEffect(() => {
    if (!towing?.ptsTowingID) return;
    let active = true;
    executeApiAction({
      dispatch,
      setLoading: (value) => {
        if (active) setLoading(value);
      },
      action: () => getVehicleTowingRecord(towing.ptsTowingID),
      onSuccess: (data) => {
        if (!active) return;
        setRecord(data);
        methods.reset({ ...VEHICLE_TOWING_DEFAULT_VALUES, ...data });
      },
    });
    return () => {
      active = false;
    };
  }, [towing, dispatch, methods]);

  return (
    <RMSCommonDialog
      open
      title={towing ? 'Edit Towing' : 'Add Towing'}
      onClose={close}
      onSave={save}
      loading={loading}
      saveDisabled={loading || !canSave}
      leftActions={
        record && (
          <RMSRecordHistoryButton
            created={record.Created}
            createdBy={record.CreatedBy}
            updated={record.Updated}
            updatedBy={record.UpdatedBy}
          />
        )
      }>
      <FormProvider {...methods}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <RMSCheckbox name="IsTowed" label="Towed" />
          </Grid>
          <Grid item xs={12}>
            <SearchPersonAutoComplete
              name="ptsDriverID"
              label="Search Driver"
              defaultPersonId={driverId}
              showDetailsIcon={false}
              disabled={!canSave || loading}
            />
          </Grid>
          <Grid item xs={12}>
            <SearchPlaceAutoComplete
              name="ptsPlaceID"
              label="Tower Reference"
              defaultPlaceId={placeId}
              showDetailsIcon={false}
              disabled={!canSave || loading}
            />
          </Grid>
        </Grid>
      </FormProvider>

      {confirmState.open && (
        <RMSConfirmationDialog
          open
          title={confirmState.title}
          message={confirmState.message}
          onYes={handleYes}
          onNo={handleNo}
        />
      )}
    </RMSCommonDialog>
  );
};
export default VehicleTowingDialog;
